import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "@finitless/shared/hooks";
import { useUserStore } from "@finitless/shared/stores";
/**
 * Component to track page views for DAU/WAU metrics
 * Should be placed inside the router to track all route changes
 */
const PageViewTracker = () => {
  const location = useLocation();
  const { trackPageView } = usePostHog();
  const user = useUserStore((state) => state.user);

  // Use ref to store user ID to avoid triggering effect when user data loads
  const userIdRef = useRef(user?.id);
  const trackPageViewRef = useRef(trackPageView);

  // Update refs when values change
  useEffect(() => {
    userIdRef.current = user?.id;
    trackPageViewRef.current = trackPageView;
  });

  useEffect(() => {
    // Map route paths to readable page names
    const getPageName = (pathname) => {
      const routeMap = {
        "/": "Overview",
        "/overview": "Overview",
        "/ai-agents": "AI Agents",
        "/orders": "Orders",
        "/analyze/conversations": "Conversations",
        "/analyze/voice-calls": "Voice Calls",
        "/analyze/reports": "Reports",
        "/users": "Customers",
        "/discounts": "Discounts",
        "/growth/cart-recovery": "Cart Recovery",
        "/growth/cart-recovery/new": "New Cart Recovery",
        "/growth/link-in-bio": "Link in Bio",
        "/growth/meta-ads": "Meta Ads",
        "/growth/meta-ads/connect": "Meta Ads — Connect",
        "/growth/meta-ads/create": "Meta Ads — Create",
        "/growth/meta-ads/success": "Meta Ads — Submitted",
        "/growth/meta-ads/performance": "Meta Ads — Performance",
        "/menu/stores": "Stores",
        "/menu/stores/create/address": "Create Business",
        "/menu/stores/create/form": "Create Business",
        "/menu/products": "Products",
        "/menu/brands": "Brands",
        "/integrations/pos": "POS Integrations",
        "/integrations/menu-importers": "Menu Importers",
        "/integrations/sales-channels": "Sales Channels",
        "/integrations/delivery-services": "Delivery Services",
        "/delivery/drivers-managers": "Drivers Managers",
        "/delivery/groups": "Delivery Groups",
        "/delivery/groups/create": "Create Delivery Group",
        "/settings/basic": "Settings (Basic)",
        "/settings/online-payments": "Settings (Online Payments)",
        "/settings/agent-translations": "Settings (Agent Translations)",
        "/settings/admins": "Admins",
        "/support": "Support",
        "/account": "My Account",
        "/campaigns": "Campaigns",
        "/campaigns/new": "New Campaign",
      };

      // Check for exact matches first
      if (routeMap[pathname]) {
        return routeMap[pathname];
      }

      // Check for dynamic routes
      if (pathname.startsWith("/orders/")) {
        return "Order Detail";
      }
      if (pathname.startsWith("/users/")) {
        return "Customer Detail";
      }
      if (pathname.startsWith("/analyze/conversations/")) {
        return "Conversation Detail";
      }
      if (pathname.startsWith("/discounts/")) {
        return "Discount Detail";
      }
      if (pathname.startsWith("/menu/stores/")) {
        return "Store Detail";
      }
      if (pathname.startsWith("/menu/products/")) {
        return "Products";
      }
      if (pathname.startsWith("/menu/brands/")) {
        return "Brand Detail";
      }
      if (pathname.startsWith("/integrations/delivery-services/")) {
        return "Delivery Service Detail";
      }
      if (pathname.startsWith("/delivery/drivers-managers/")) {
        return "Driver Manager Detail";
      }
      if (pathname.startsWith("/delivery/groups/")) {
        return "Delivery Group Detail";
      }
      if (pathname.startsWith("/settings/admins/")) {
        return "Admin Detail";
      }
      if (pathname.startsWith("/growth/cart-recovery/")) {
        return "Cart Recovery Detail";
      }
      if (pathname.startsWith("/growth/link-in-bio")) {
        return "Link in Bio";
      }
      if (pathname.startsWith("/growth/meta-ads/edit/")) {
        return "Meta Ads — Edit campaign";
      }
      if (pathname.startsWith("/growth/meta-ads")) {
        return "Meta Ads";
      }

      return pathname;
    };

    const pageName = getPageName(location.pathname);

    // Track page view for DAU/WAU metrics
    // Use ref values to avoid duplicate tracking when user data loads or PostHog initializes
    trackPageViewRef.current(pageName, {
      path: location.pathname,
      search: location.search,
      user_id: userIdRef.current,
    });
  }, [location]);

  return null;
};

export default PageViewTracker;
